import { Type, type Static } from "@sinclair/typebox";
import { ProjectIdSchema, RoomIdSchema, UuidSchema } from "./identifiers.ts";
import { JsonObjectSchema } from "./json-value.ts";
import { ProductActionSchema } from "./product-authorization.ts";

export const DOCUMENT_PLATFORM_SCHEMA_VERSION = "1.0" as const;
export const TEMPORARY_FILE_REFERENCE_LIMIT = 16;
export const TEMPORARY_FILE_MAX_BYTES = 52_428_800;

export const DOCUMENT_PROCESSING_SUPPORTED_MEDIA_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/markdown"
] as const;

export type DocumentProcessingSupportedMediaTypeV1 =
  (typeof DOCUMENT_PROCESSING_SUPPORTED_MEDIA_TYPES)[number];

export const DocumentProcessingSupportedMediaTypeSchema = Type.Union([
  Type.Literal("application/pdf"),
  Type.Literal("application/vnd.openxmlformats-officedocument.wordprocessingml.document"),
  Type.Literal("text/markdown")
]);

const TimestampSchema = Type.String({ format: "date-time" });
const ChecksumSchema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const ETagSchema = Type.String({ minLength: 1, maxLength: 256 });

export const PrincipalTypeSchema = Type.Union([
  Type.Literal("human"),
  Type.Literal("agent"),
  Type.Literal("service")
]);
export type PrincipalTypeV1 = Static<typeof PrincipalTypeSchema>;

export const PrincipalSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    principalId: UuidSchema,
    principalType: PrincipalTypeSchema,
    serviceId: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type PrincipalV1 = Static<typeof PrincipalSchema>;

export const ProjectSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    projectId: ProjectIdSchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    createdByPrincipalId: UuidSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type ProjectV1 = Static<typeof ProjectSchema>;

export const ProjectCreateInputSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 256 }),
    idempotencyKey: Type.String({ minLength: 1, maxLength: 128 })
  },
  { additionalProperties: false }
);
export type ProjectCreateInputV1 = Static<typeof ProjectCreateInputSchema>;

export const DocumentLifecycleStatusSchema = Type.Union([
  Type.Literal("active"),
  Type.Literal("trashed"),
  Type.Literal("missing"),
  Type.Literal("archived")
]);
export type DocumentLifecycleStatusV1 = Static<typeof DocumentLifecycleStatusSchema>;

export const DocumentRepresentationStatusSchema = Type.Union([
  Type.Literal("absent"),
  Type.Literal("current"),
  Type.Literal("stale")
]);
export type DocumentRepresentationStatusV1 = Static<typeof DocumentRepresentationStatusSchema>;

export const DocumentSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    documentId: UuidSchema,
    fileSpaceId: UuidSchema,
    openCloudResourceId: Type.String({ minLength: 1, maxLength: 1024 }),
    path: Type.String({ minLength: 1, maxLength: 1024 }),
    name: Type.String({ minLength: 1, maxLength: 512 }),
    mediaType: Type.String({ minLength: 1, maxLength: 256 }),
    sizeBytes: Type.Integer({ minimum: 0 }),
    sourceETag: ETagSchema,
    sourceChecksum: Type.Union([ChecksumSchema, Type.Null()]),
    lifecycleStatus: DocumentLifecycleStatusSchema,
    representationStatus: DocumentRepresentationStatusSchema,
    createdByPrincipalId: Type.Union([UuidSchema, Type.Null()]),
    sourceObservedAt: TimestampSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentV1 = Static<typeof DocumentSchema>;

export const FileSpaceDocumentSchema = Type.Object(
  {
    ...DocumentSchema.properties,
    knowledgeBaseUsageCount: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type FileSpaceDocumentV1 = Static<typeof FileSpaceDocumentSchema>;

export const DocumentPageSchema = Type.Object(
  {
    pageNumber: Type.Integer({ minimum: 1 }),
    width: Type.Number({ exclusiveMinimum: 0 }),
    height: Type.Number({ exclusiveMinimum: 0 }),
    markdownStart: Type.Integer({ minimum: 0 }),
    markdownEnd: Type.Integer({ minimum: 1 })
  },
  { additionalProperties: false }
);
export type DocumentPageV1 = Static<typeof DocumentPageSchema>;

export const DocumentImageSchema = Type.Object(
  {
    imageId: Type.String({ minLength: 1, maxLength: 512 }),
    contentSha256: ChecksumSchema,
    mediaType: Type.Union([
      Type.Literal("image/png"),
      Type.Literal("image/jpeg"),
      Type.Literal("image/webp")
    ]),
    width: Type.Integer({ minimum: 1 }),
    height: Type.Integer({ minimum: 1 }),
    sizeBytes: Type.Integer({ minimum: 1 }),
    bucketName: Type.String({ minLength: 3, maxLength: 63 }),
    objectKey: Type.String({ minLength: 1, maxLength: 2048 }),
    etag: Type.String({ minLength: 1, maxLength: 256 }),
    occurrenceId: Type.Union([Type.String({ minLength: 1, maxLength: 512 }), Type.Null()]),
    ordinal: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    pageNumber: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    bbox: Type.Union([Type.Unknown(), Type.Null()]),
    sourceCaption: Type.Union([Type.String({ minLength: 1, maxLength: 4096 }), Type.Null()]),
    markdownStart: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    markdownEnd: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()])
  },
  { additionalProperties: false }
);
export type DocumentImageV1 = Static<typeof DocumentImageSchema>;

export const DocumentRepresentationSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    representationId: UuidSchema,
    documentId: UuidSchema,
    processingTaskId: Type.Union([UuidSchema, Type.Null()]),
    sourceETag: ETagSchema,
    sourceChecksum: Type.Union([ChecksumSchema, Type.Null()]),
    canonicalMarkdown: Type.String({ minLength: 1, maxLength: 50_000_000 }),
    tables: Type.Array(Type.Unknown(), { maxItems: 100_000 }),
    images: Type.Array(DocumentImageSchema, { maxItems: 100_000 }),
    pages: Type.Union([Type.Array(DocumentPageSchema, { maxItems: 100_000 }), Type.Null()]),
    structureAnchors: Type.Array(Type.Unknown(), { maxItems: 200_000 }),
    diagnostics: Type.Array(Type.Unknown(), { maxItems: 1024 }),
    resultChecksum: ChecksumSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentRepresentationV1 = Static<typeof DocumentRepresentationSchema>;

export const DocumentUploadMetadataSchema = Type.Object(
  {
    fileName: Type.String({
      minLength: 1,
      maxLength: 512,
      pattern: "^(?!\\.{1,2}$)[^/\\\\]+$"
    }),
    mediaType: DocumentProcessingSupportedMediaTypeSchema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: 52_428_800 }),
    contentSha256: ChecksumSchema
  },
  { additionalProperties: false }
);
export type DocumentUploadMetadataV1 = Static<typeof DocumentUploadMetadataSchema>;

export const DocumentProcessingTaskStatusSchema = Type.Union([
  Type.Literal("queued"),
  Type.Literal("running"),
  Type.Literal("succeeded"),
  Type.Literal("failed"),
  Type.Literal("cancelled")
]);
export type DocumentProcessingTaskStatusV1 = Static<typeof DocumentProcessingTaskStatusSchema>;

export const DocumentProcessingTaskSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    taskId: UuidSchema,
    documentId: UuidSchema,
    sourceETag: ETagSchema,
    sourceChecksum: Type.Union([ChecksumSchema, Type.Null()]),
    status: DocumentProcessingTaskStatusSchema,
    attemptCount: Type.Integer({ minimum: 0 }),
    maxAttempts: Type.Integer({ minimum: 1, maximum: 100 }),
    errorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    errorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2048 }), Type.Null()]),
    errorDetails: Type.Union([JsonObjectSchema, Type.Null()]),
    requestedByPrincipalId: UuidSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentProcessingTaskV1 = Static<typeof DocumentProcessingTaskSchema>;

export const DocumentProcessingTaskPageSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    items: Type.Array(DocumentProcessingTaskSchema, { maxItems: 100 })
  },
  { additionalProperties: false }
);
export type DocumentProcessingTaskPageV1 = Static<typeof DocumentProcessingTaskPageSchema>;

export const DocumentProcessingTaskObservationSchema = Type.Object(
  {
    ...DocumentProcessingTaskSchema.properties,
    fileSpaceId: UuidSchema,
    documentName: Type.String({ minLength: 1, maxLength: 512 }),
    documentPath: Type.String({ minLength: 1, maxLength: 1024 }),
    failedParts: Type.Integer({ minimum: 0 }),
    finalizationRetryAvailable: Type.Boolean()
  },
  { additionalProperties: false }
);
export type DocumentProcessingTaskObservationV1 = Static<
  typeof DocumentProcessingTaskObservationSchema
>;

export const DocumentProcessingTaskObservationQuerySchema = Type.Object(
  {
    fileSpaceId: Type.Optional(UuidSchema),
    status: Type.Optional(DocumentProcessingTaskStatusSchema),
    attentionOnly: Type.Optional(Type.Boolean()),
    createdAfter: Type.Optional(TimestampSchema),
    offset: Type.Optional(Type.Integer({ minimum: 0, maximum: 1_000_000 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 }))
  },
  { additionalProperties: false }
);
export type DocumentProcessingTaskObservationQueryV1 = Static<
  typeof DocumentProcessingTaskObservationQuerySchema
>;

export const DocumentProcessingTaskObservationPageSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    items: Type.Array(DocumentProcessingTaskObservationSchema, { maxItems: 100 }),
    nextOffset: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()])
  },
  { additionalProperties: false }
);
export type DocumentProcessingTaskObservationPageV1 = Static<
  typeof DocumentProcessingTaskObservationPageSchema
>;

const FailedExtractionPartOrdinalsSchema = Type.Array(Type.Integer({ minimum: 0 }), {
  minItems: 1,
  maxItems: 10_000,
  uniqueItems: true
});

export const DocumentExtractionPartRetryInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    partOrdinals: Type.Optional(FailedExtractionPartOrdinalsSchema)
  },
  { additionalProperties: false }
);
export type DocumentExtractionPartRetryInputV1 = Static<
  typeof DocumentExtractionPartRetryInputSchema
>;

export const DocumentExtractionPartSkipInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    confirmed: Type.Literal(true),
    partOrdinals: Type.Optional(FailedExtractionPartOrdinalsSchema)
  },
  { additionalProperties: false }
);
export type DocumentExtractionPartSkipInputV1 = Static<
  typeof DocumentExtractionPartSkipInputSchema
>;

export const DocumentExtractionFinalizationRetryInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION)
  },
  { additionalProperties: false }
);
export type DocumentExtractionFinalizationRetryInputV1 = Static<
  typeof DocumentExtractionFinalizationRetryInputSchema
>;

export const DocumentExtractionPartResolutionSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    task: DocumentProcessingTaskSchema,
    partOrdinals: FailedExtractionPartOrdinalsSchema,
    remainingFailedParts: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type DocumentExtractionPartResolutionV1 = Static<
  typeof DocumentExtractionPartResolutionSchema
>;

/** Wire phases written by Document Extraction checkpoints. */
export const DocumentExtractionPhaseSchema = Type.Union([
  Type.Literal("queued"),
  Type.Literal("local_document_analysis"),
  Type.Literal("normalizing"),
  Type.Literal("waiting_for_visual_model"),
  Type.Literal("visual_page_analysis"),
  Type.Literal("checkpointing"),
  Type.Literal("finalizing"),
  Type.Literal("completed"),
  Type.Literal("failed")
]);
export type DocumentExtractionPhaseV1 = Static<typeof DocumentExtractionPhaseSchema>;

export const DocumentExtractionPartStatusSchema = Type.Union([
  Type.Literal("pending"),
  Type.Literal("running"),
  Type.Literal("succeeded"),
  Type.Literal("skipped"),
  Type.Literal("failed")
]);
export type DocumentExtractionPartStatusV1 = Static<typeof DocumentExtractionPartStatusSchema>;

export const DocumentExtractionVisualStatusSchema = Type.Union([
  Type.Literal("not_required"),
  Type.Literal("pending"),
  Type.Literal("running"),
  Type.Literal("succeeded"),
  Type.Literal("failed")
]);
export type DocumentExtractionVisualStatusV1 = Static<typeof DocumentExtractionVisualStatusSchema>;

export const DocumentExtractionPartProgressSchema = Type.Object(
  {
    partOrdinal: Type.Integer({ minimum: 0 }),
    pageStart: Type.Integer({ minimum: 1 }),
    pageEnd: Type.Integer({ minimum: 1 }),
    status: DocumentExtractionPartStatusSchema,
    phase: DocumentExtractionPhaseSchema,
    visualRequired: Type.Boolean(),
    visualStatus: DocumentExtractionVisualStatusSchema,
    visualTotalItems: Type.Integer({ minimum: 0 }),
    visualCompletedItems: Type.Integer({ minimum: 0 }),
    attemptCount: Type.Integer({ minimum: 0 }),
    contentCharacters: Type.Integer({ minimum: 0 }),
    errorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    errorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2048 }), Type.Null()]),
    errorRetryable: Type.Union([Type.Boolean(), Type.Null()]),
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentExtractionPartProgressV1 = Static<typeof DocumentExtractionPartProgressSchema>;

/**
 * Live Document Extraction progress for one Processing Task.
 * Absent run means the Task has not yet initialized checkpoints (still queued / starting).
 */
export const DocumentExtractionProgressSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    taskId: UuidSchema,
    documentId: UuidSchema,
    runStatus: Type.Union([
      Type.Literal("running"),
      Type.Literal("succeeded"),
      Type.Literal("failed"),
      Type.Null()
    ]),
    phase: Type.Union([DocumentExtractionPhaseSchema, Type.Null()]),
    totalPages: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    completedPages: Type.Integer({ minimum: 0 }),
    totalParts: Type.Integer({ minimum: 0 }),
    completedParts: Type.Integer({ minimum: 0 }),
    pendingParts: Type.Integer({ minimum: 0 }),
    runningParts: Type.Integer({ minimum: 0 }),
    failedParts: Type.Integer({ minimum: 0 }),
    extractedCharacters: Type.Integer({ minimum: 0 }),
    visualRequired: Type.Boolean(),
    visualStatus: DocumentExtractionVisualStatusSchema,
    visualTotalItems: Type.Integer({ minimum: 0 }),
    visualCompletedItems: Type.Integer({ minimum: 0 }),
    parts: Type.Array(DocumentExtractionPartProgressSchema, { maxItems: 10_000 }),
    lastErrorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    lastErrorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2048 }), Type.Null()]),
    lastErrorRetryable: Type.Union([Type.Boolean(), Type.Null()]),
    updatedAt: Type.Union([TimestampSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type DocumentExtractionProgressV1 = Static<typeof DocumentExtractionProgressSchema>;

/** Succeeded / skipped Part checkpoint content for in-progress page preview. */
export const DocumentExtractionPartContentSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    taskId: UuidSchema,
    partOrdinal: Type.Integer({ minimum: 0 }),
    pageStart: Type.Integer({ minimum: 1 }),
    pageEnd: Type.Integer({ minimum: 1 }),
    status: Type.Union([Type.Literal("succeeded"), Type.Literal("skipped")]),
    markdownBlocks: Type.Array(Type.String({ minLength: 1, maxLength: 5_000_000 }), {
      maxItems: 1_000_000
    }),
    tables: Type.Array(Type.Unknown(), { maxItems: 100_000 }),
    diagnostics: Type.Array(Type.Unknown(), { maxItems: 1_024 }),
    contentCharacters: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type DocumentExtractionPartContentV1 = Static<typeof DocumentExtractionPartContentSchema>;

export const AuthorizationContextSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    authorizationContextId: UuidSchema,
    principalId: UuidSchema,
    action: ProductActionSchema,
    projectId: Type.Union([ProjectIdSchema, Type.Null()]),
    fileSpaceId: UuidSchema,
    knowledgeBaseId: Type.Union([UuidSchema, Type.Null()]),
    documentIds: Type.Array(UuidSchema, { minItems: 1, maxItems: 10_000, uniqueItems: true }),
    permissionsFingerprint: ChecksumSchema,
    issuedAt: TimestampSchema,
    expiresAt: TimestampSchema,
    keyId: Type.String({ minLength: 1, maxLength: 128 }),
    algorithm: Type.Literal("Ed25519"),
    signature: Type.String({ minLength: 86, maxLength: 128 })
  },
  { additionalProperties: false }
);
export type AuthorizationContextV1 = Static<typeof AuthorizationContextSchema>;

export const TemporaryFileCreateInputSchema = Type.Object(
  {
    roomId: RoomIdSchema,
    fileName: Type.String({ minLength: 1, maxLength: 512, pattern: "^(?!\\.{1,2}$)[^/\\\\]+$" }),
    mediaType: Type.String({ minLength: 1, maxLength: 256 }),
    sizeBytes: Type.Integer({ minimum: 1, maximum: TEMPORARY_FILE_MAX_BYTES }),
    contentSha256: ChecksumSchema
  },
  { additionalProperties: false }
);
export type TemporaryFileCreateInputV1 = Static<typeof TemporaryFileCreateInputSchema>;

export const TemporaryFileUploadIntentSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    temporaryFileId: UuidSchema,
    bucketName: Type.Literal("temporary-files"),
    objectKey: Type.String({ minLength: 1, maxLength: 1024 }),
    uploadToken: Type.String({ minLength: 1, maxLength: 4096 }),
    expiresAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type TemporaryFileUploadIntentV1 = Static<typeof TemporaryFileUploadIntentSchema>;

export const TemporaryFileSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    temporaryFileId: UuidSchema,
    roomId: RoomIdSchema,
    createdByPrincipalId: UuidSchema,
    fileName: Type.String({ minLength: 1, maxLength: 512 }),
    mediaType: Type.String({ minLength: 1, maxLength: 256 }),
    sizeBytes: Type.Integer({ minimum: 1, maximum: TEMPORARY_FILE_MAX_BYTES }),
    contentSha256: ChecksumSchema,
    expiresAt: TimestampSchema,
    createdAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type TemporaryFileV1 = Static<typeof TemporaryFileSchema>;

export const TemporaryFileReferenceSchema = Type.Object(
  {
    temporaryFileId: UuidSchema,
    name: Type.String({
      minLength: 1,
      maxLength: 512,
      pattern: "^(?!\\.{1,2}$)[^/\\\\\\u0000-\\u001f\\u007f]+$"
    })
  },
  { additionalProperties: false }
);
export type TemporaryFileReferenceV1 = Static<typeof TemporaryFileReferenceSchema>;

export const TemporaryFileReferencesSchema = Type.Array(TemporaryFileReferenceSchema, {
  maxItems: TEMPORARY_FILE_REFERENCE_LIMIT
});
export type TemporaryFileReferencesV1 = Static<typeof TemporaryFileReferencesSchema>;

export const SignedDownloadSchema = Type.Object(
  {
    url: Type.String({ format: "uri" }),
    expiresAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type SignedDownloadV1 = Static<typeof SignedDownloadSchema>;
